// SPDX-License-Identifier: AGPL-3.0-or-later

import {Slider} from '@app/features/ui/components/Slider';
import styles from '@app/features/voice_scene/components/VoiceSceneAmbienceControl.module.css';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {SpeakerHighIcon, SpeakerXIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

const ROOM_SOUND_DESCRIPTOR = msg({
	message: 'Room sound',
	comment:
		"Voice call 3D room: label and accessible name of the slider for how loud the room's own background sound (rain, café murmur) plays. Remembered on this device.",
});
const ROOM_SOUND_VALUE_DESCRIPTOR = msg({
	message: '{percent}%',
	comment: 'Voice call 3D room: spoken value of the room sound slider. {percent} is a number from 1 to 100.',
});
const ROOM_SOUND_OFF_DESCRIPTOR = msg({
	message: 'Off',
	context: 'voice-scene-room-sound',
	comment: 'Voice call 3D room: spoken value of the room sound slider at 0, where the room sound is off.',
});

// How loud the room's ambience plays, 0 (off) to 100; the setting itself is 0 to 1.
export const VoiceSceneAmbienceControl = observer(function VoiceSceneAmbienceControl() {
	const {i18n} = useLingui();
	const label = i18n._(ROOM_SOUND_DESCRIPTOR);
	const percent = Math.round(VoiceSceneSettings.ambienceVolume * 100);
	const Icon = percent === 0 ? SpeakerXIcon : SpeakerHighIcon;
	return (
		<div className={styles.control} data-flx="voice-scene.voice-scene-ambience-control.control">
			<Icon className={styles.icon} aria-hidden="true" data-flx="voice-scene.voice-scene-ambience-control.icon" />
			<span className={styles.label} data-flx="voice-scene.voice-scene-ambience-control.label">
				{label}
			</span>
			<div className={styles.slider} data-flx="voice-scene.voice-scene-ambience-control.slider-wrap">
				<Slider
					mini
					value={percent}
					defaultValue={percent}
					factoryDefaultValue={50}
					minValue={0}
					maxValue={100}
					step={1}
					ariaLabel={label}
					ariaValueText={
						percent === 0 ? i18n._(ROOM_SOUND_OFF_DESCRIPTOR) : i18n._(ROOM_SOUND_VALUE_DESCRIPTOR, {percent})
					}
					onValueChange={(value) => VoiceSceneSettings.setAmbienceVolume(Math.round(value) / 100)}
					data-flx="voice-scene.voice-scene-ambience-control.slider"
				/>
			</div>
		</div>
	);
});
